import { useMemo, useState } from "react";
import Card from "../Card";
import BuySellModal from "../BuySellModal";
import SwapModal from "../SwapModal";
import TransferModal from "../TransferModal";
import PercentChange from "../PercentChange";
import Sparkline from "../Sparkline";
import { formatLargeUsd, formatMarketPrice } from "../../utils/format";
import MarketPriceDetailModal from "./MarketPriceDetailModal";

const headerClass =
  "text-sm font-medium leading-[19.5px] text-text-primary whitespace-nowrap";

// const SKELETON_ROWS = Array.from({ length: 8 });

const MarketPricesTable = ({
  marketPricesData = [],
  loading = false,
  cryptoAccounts = [],
}) => {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [fromAsset, setFromAsset] = useState(null);
  const [isBuySellModalOpen, setIsBuySellModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const isNewUser = !cryptoAccounts?.some(
    (account) => account?.cryptoAddresses?.length > 0
  );
  const isSomeBalance = cryptoAccounts?.some((account) => account?.balance > 0);

  // Map incoming CoinGecko response objects to table rows
  const formattedPrices = useMemo(() => {
    if (!Array.isArray(marketPricesData)) return [];

    return marketPricesData.map((item, index) => {
      const price = Number(item?.current_price ?? 0);
      const change24h = Number(item?.price_change_percentage_24h_in_currency ?? 0);
      const is24hPositive = change24h >= 0;

      // Extract 7-day sparkline array from CoinGecko payload
      const sparklinePrices = item?.sparkline_in_7d?.price ?? [];

      // Derive 1h % change and 7d % change if provided, or calculate from sparkline history
      const change1h = Number(item?.price_change_percentage_1h_in_currency ?? 0);
      const change7d = Number(
        item?.price_change_percentage_7d_in_currency ??
          (sparklinePrices.length > 0
            ? ((sparklinePrices[sparklinePrices.length - 1] - sparklinePrices[0]) /
                sparklinePrices[0]) *
              100
            : 0)
      );

      const is7dPositive = change7d >= 0;
      
      // Format volume quantity label (e.g. 234.56K BTC)
      const volumeTokenCount = price > 0 ? (item?.total_volume ?? 0) / price : 0;
      const volumeLabel = `${(volumeTokenCount / 1000).toFixed(2)}K ${item?.symbol?.toUpperCase()}`;

      // const sparklineColor = is24hPositive ? "#4AB932" : "#EF4444";
      const sparklineColor = is7dPositive ? "#4AB932" : "#EF4444";

      return {
        id: item?.id ?? index,
        rank: item?.market_cap_rank ?? index + 1,
        name: item?.name ?? "",
        symbol: item?.symbol?.toUpperCase() ?? "",
        logo: item?.image ?? "",
        price: price,
        change1h: Math.abs(change1h),
        change1hPositive: change1h >= 0,
        change24h: Math.abs(change24h),
        positive: is24hPositive,
        change7d: Math.abs(change7d),
        change7dPositive: change7d >= 0,
        marketCap: Number(item?.market_cap ?? 0),
        volumeUsd: Number(item?.total_volume ?? 0),
        volumeLabel: volumeLabel,
        sparkline: sparklinePrices,
        color: sparklineColor,
        raw: item,
      };
    });
  }, [marketPricesData]);

  const openBuySellModal = () => {
    setSelectedAsset(null);
    setIsBuySellModalOpen(true);
  };

  const openTransferModal = () => {
    setSelectedAsset(null);
    setIsTransferModalOpen(true);
  };

  const openSwapModal = () => {
    setSelectedAsset(null);
    setIsSwapModalOpen(true);
  };

  return (
    <>
      <Card className="flex flex-col overflow-hidden p-0 font-outfit">
        <div className="flex h-[54px] shrink-0 items-center justify-between border-b border-divider px-4 pb-[17px] pt-4">
          <h2 className="text-base font-semibold leading-[21px] text-text-primary">
            Market prices
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse">
            <thead>
              <tr className="border-b border-divider">
                <th className={`${headerClass} px-5 py-4 text-left`} colSpan={2}>
                  <span className="mr-9">#</span>
                  <span>Name</span>
                </th>
                <th className={`${headerClass} px-3 py-4 text-left`}>Price</th>
                <th className={`${headerClass} px-3 py-4 text-right`}>1h %</th>
                <th className={`${headerClass} px-3 py-4 text-right`}>24h %</th>
                <th className={`${headerClass} px-3 py-4 text-right`}>7d %</th>
                <th className={`${headerClass} px-3 py-4 text-left`}>Market cap</th>
                <th className={`${headerClass} px-3 py-4 text-left`}>Volume (24h)</th>
                <th className={`${headerClass} px-5 py-4 text-right`}>Last 7 days</th>
              </tr>
            </thead>
            <tbody>
              {/* Loading State */}
              {formattedPrices.length === 0 ? (
                /* Empty State */
                <tr>
                  <td colSpan={9} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <div className="flex size-12 items-center justify-center rounded-full bg-divider text-text-secondary">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="size-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.281m5.94 2.28l-2.28 5.941"
                          />
                        </svg>
                      </div>
                      <p className="text-sm font-semibold text-text-primary">
                        No market data available
                      </p>
                      <p className="max-w-xs text-xs text-text-secondary">
                        There are currently no assets or market prices to display. Please check back later.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                /* Data Rows */
                formattedPrices.map((item, index) => (
                  <tr
                    key={item.id}
                    onClick={() => {
                      setSelectedAsset(item);

                      const matchedAccount =
                        cryptoAccounts?.find(
                          (acc) =>
                            acc?.currency_code?.toUpperCase() ===
                            item?.symbol?.toUpperCase()
                        ) || null;

                      setFromAsset(matchedAccount);
                    }}
                    className={`cursor-pointer custom-transition hover:bg-option-hover ${
                      index < formattedPrices.length - 1
                        ? "border-b border-divider"
                        : ""
                    }`}
                  >
                    {/* Rank */}
                    <td className="whitespace-nowrap px-5 py-2.5 text-sm font-medium text-text-primary">
                      {item.rank}
                    </td>

                    {/* Logo & Name */}
                    <td className="whitespace-nowrap py-2.5 pr-3">
                      <div className="flex min-w-[120px] items-center gap-2.5">
                        <div className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-background-alt">
                          <img
                            src={item.logo}
                            alt={item.name}
                            className="size-6 rounded-full object-cover"
                          />
                        </div>
                        <span className="text-sm font-semibold text-text-primary">
                          {item.name}
                        </span>
                      </div>
                    </td>

                    {/* Current Price */}
                    <td className="whitespace-nowrap px-3 py-2.5 text-sm font-medium text-text-primary">
                      {formatMarketPrice(item.price)}
                    </td>

                    {/* 1h % Change */}
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      <PercentChange
                        value={item.change1h}
                        positive={item.change1hPositive}
                      />
                    </td>

                    {/* 24h % Change */}
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      <PercentChange
                        value={item.change24h}
                        positive={item.positive}
                      />
                    </td>

                    {/* 7d % Change */}
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      <PercentChange
                        value={item.change7d}
                        positive={item.change7dPositive}
                      />
                    </td>

                    {/* Market Cap */}
                    <td className="whitespace-nowrap px-3 py-2.5 text-sm font-medium text-text-primary">
                      {formatLargeUsd(item.marketCap)}
                    </td>

                    {/* Volume (24h) */}
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <p className="text-sm font-medium text-text-primary">
                        {formatLargeUsd(item.volumeUsd)}
                      </p>
                      <p className="text-xs text-text-secondary">
                        {item.volumeLabel}
                      </p>
                    </td>

                    {/* Last 7 days Sparkline */}
                    <td className="px-5 py-2.5">
                      <div className="flex justify-end">
                        {item.sparkline.length > 0 && (
                          <Sparkline
                            data={item.sparkline}
                            color={item.color}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <MarketPriceDetailModal
        asset={selectedAsset}
        isOpen={Boolean(selectedAsset)}
        onClose={() => setSelectedAsset(null)}
        onBuySell={openBuySellModal}
        onTransfer={openTransferModal}
        onSwap={openSwapModal}
      />

      <BuySellModal
        isOpen={isBuySellModalOpen}
        onClose={() => setIsBuySellModalOpen(false)}
        showBuyGuide={isNewUser}
        totalBalanceUsd={isSomeBalance}
        onGuideDepositCrypto={() => {
          setIsBuySellModalOpen(false);
          setIsTransferModalOpen(true);
        }}
      />

      <TransferModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        isCreateNewWallet
        showDepositGuide={isNewUser}
        isNewUser={isNewUser}
        totalBalanceUsd={isSomeBalance}
        defaultTab={isSomeBalance ? "Withdraw" : "Deposit"}
        beneficiaryAsset={fromAsset}
        defaultStep={fromAsset ? 2 : 1}
        onGuideBuyCrypto={() => {
          setIsTransferModalOpen(false);
          setIsBuySellModalOpen(true);
        }}
      />

      <SwapModal
        isOpen={isSwapModalOpen}
        onClose={() => setIsSwapModalOpen(false)}
        cryptoAccounts={cryptoAccounts}
        selectedfromAsset={fromAsset}
      />
    </>
  );
};

export default MarketPricesTable;